import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

interface IdCardState {
  data: any | null;
  masterData: any[] | null;
  detailData: any | null;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "upload" | "create" | "inclusion" | "deletion" | "sync" | "master" | "findOne" | "approveReject" | null;
}

const initialState: IdCardState = {
  data: null,
  masterData: null,
  detailData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getIdCardData = createAsyncThunk(
  "idCard/list",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.FIND_ALL, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchIdCardList = createAsyncThunk(
  "idCard/fetchList",
  async (
    payload: { page: number; limit: number; startDate: string; endDate: string; status: string; empId: string },
    thunkAPI
  ) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.FIND_ALL, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const syncIdCard = createAsyncThunk(
  "idCard/sync",
  async (empId: string, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.SYNC, { empId });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const fetchIdCardMaster = createAsyncThunk(
  "idCard/master",
  async (_: void, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.MASTER, {});
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const findOneIdCard = createAsyncThunk(
  "idCard/findOne",
  async (id: string | number, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.FIND_ONE, { id });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const uploadPhoto = createAsyncThunk(
  "idCard/uploadPhoto",
  async (requestBody: { filestring: string }, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.UPLOAD_PHOTO, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createIdCardRequest = createAsyncThunk(
  "idCard/create",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.CREATE, requestBody);
      const resData = response?.data;
      const wasCreated =
        resData?.id ||
        resData?.ReferenceNumber ||
        resData?.data?.id ||
        resData?.data?.ReferenceNumber ||
        resData?.data?.data?.id ||
        resData?.data?.data?.ReferenceNumber;

      if (wasCreated || resData?.success === true || resData?.status === 200 || resData?.status === 201) {
        return resData;
      }

      if (resData?.success === false || resData?.status >= 400) {
        return thunkAPI.rejectWithValue(
          resData?.message || resData?.error || resData?.data?.message || "Failed to submit application"
        );
      }

      return resData;
    } catch (error: any) {
      const errResData = error?.response?.data;
      const wasCreated =
        errResData?.id ||
        errResData?.ReferenceNumber ||
        errResData?.data?.id ||
        errResData?.data?.ReferenceNumber ||
        errResData?.data?.data?.id ||
        errResData?.data?.data?.ReferenceNumber;

      if (wasCreated) {
        return errResData;
      }
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createOrUpdateIdCard = createAsyncThunk(
  "idCard/createOrUpdate",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.CREATE, requestBody);
      const resData = response?.data;
      const wasCreated =
        resData?.id ||
        resData?.ReferenceNumber ||
        resData?.data?.id ||
        resData?.data?.ReferenceNumber ||
        resData?.data?.data?.id ||
        resData?.data?.data?.ReferenceNumber;

      if (wasCreated || resData?.success === true || resData?.status === 200 || resData?.status === 201) {
        return resData;
      }

      if (resData?.success === false || resData?.status >= 400) {
        return thunkAPI.rejectWithValue(
          resData?.message || resData?.error || resData?.data?.message || "Failed to submit application"
        );
      }

      return resData;
    } catch (error: any) {
      const errResData = error?.response?.data;
      const wasCreated =
        errResData?.id ||
        errResData?.ReferenceNumber ||
        errResData?.data?.id ||
        errResData?.data?.ReferenceNumber ||
        errResData?.data?.data?.id ||
        errResData?.data?.data?.ReferenceNumber;

      if (wasCreated) {
        return errResData;
      }
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const saveIdCardDraft = createAsyncThunk(
  "idCard/saveDraft",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.CREATE, { ...requestBody, is_draft: true });
      const resData = response?.data;
      const wasCreated =
        resData?.id ||
        resData?.ReferenceNumber ||
        resData?.data?.id ||
        resData?.data?.ReferenceNumber ||
        resData?.data?.data?.id ||
        resData?.data?.data?.ReferenceNumber;

      if (wasCreated || resData?.success === true || resData?.status === 200 || resData?.status === 201) {
        return resData;
      }

      if (resData?.success === false || resData?.status >= 400) {
        return thunkAPI.rejectWithValue(
          resData?.message || resData?.error || resData?.data?.message || "Failed to save draft"
        );
      }

      return resData;
    } catch (error: any) {
      const errResData = error?.response?.data;
      const wasCreated =
        errResData?.id ||
        errResData?.ReferenceNumber ||
        errResData?.data?.id ||
        errResData?.data?.ReferenceNumber ||
        errResData?.data?.data?.id ||
        errResData?.data?.data?.ReferenceNumber;

      if (wasCreated) {
        return errResData;
      }
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const approveRejectIdCard = createAsyncThunk(
  "idCard/approveReject",
  async (
    payload: { ReferenceNumber: string; Action: "A" | "R"; HRemarks: string },
    thunkAPI
  ) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.APPROVE_REJECT_ACTION, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const inclusionOfDependent = createAsyncThunk(
  "idCard/inclusionOfDependent",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.DEPENDENT_INCLUSION, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deletionOfDependent = createAsyncThunk(
  "idCard/deletionOfDependent",
  async (requestBody: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.ID_CARD.DEPENDENT_DELETION, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const idCardSlice = createSlice({
  name: "idCard",
  initialState: initialState,
  reducers: {
    resetAll: (state) => {
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
    clearDetailData: (state) => {
      state.detailData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get ID Card List (legacy)
      .addCase(getIdCardData.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
      })
      .addCase(getIdCardData.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.error = null;
      })
      .addCase(getIdCardData.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Fetch ID Card List (paginated)
      .addCase(fetchIdCardList.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
      })
      .addCase(fetchIdCardList.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchIdCardList.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Sync
      .addCase(syncIdCard.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "sync";
      })
      .addCase(syncIdCard.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(syncIdCard.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Master data
      .addCase(fetchIdCardMaster.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "master";
      })
      .addCase(fetchIdCardMaster.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.masterData = action.payload;
        state.error = null;
      })
      .addCase(fetchIdCardMaster.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Find One
      .addCase(findOneIdCard.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "findOne";
      })
      .addCase(findOneIdCard.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.detailData = action.payload;
        state.error = null;
      })
      .addCase(findOneIdCard.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Upload Photo
      .addCase(uploadPhoto.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "upload";
      })
      .addCase(uploadPhoto.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(uploadPhoto.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Create Request (legacy)
      .addCase(createIdCardRequest.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "create";
      })
      .addCase(createIdCardRequest.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(createIdCardRequest.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Create or Update
      .addCase(createOrUpdateIdCard.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "create";
      })
      .addCase(createOrUpdateIdCard.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(createOrUpdateIdCard.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Approve / Reject
      .addCase(approveRejectIdCard.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "approveReject";
      })
      .addCase(approveRejectIdCard.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(approveRejectIdCard.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Inclusion
      .addCase(inclusionOfDependent.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "inclusion";
      })
      .addCase(inclusionOfDependent.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(inclusionOfDependent.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      })
      // Deletion
      .addCase(deletionOfDependent.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "deletion";
      })
      .addCase(deletionOfDependent.fulfilled, (state) => {
        state.loading = "fulfilled";
        state.error = null;
      })
      .addCase(deletionOfDependent.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      });
  },
});

export const { resetAll, clearDetailData } = idCardSlice.actions;
export default idCardSlice.reducer;
